import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { XMarkIcon, TrashIcon, ExclamationTriangleIcon } from '@heroicons/react/24/outline';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';

import { savedAPI, createQueryKey } from '../services/api';
import { useMobile } from '../hooks/useMobile';

const SavedListModal = ({ isOpen, onClose, list = null, mode = 'create' }) => {
  const { isMobile } = useMobile();
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [isPublic, setIsPublic] = useState(true);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const queryClient = useQueryClient();
  const navigate = useNavigate();

  // Initialize form data when list changes
  useEffect(() => {
    if (list) {
      setName(list.name || '');
      setDescription(list.description || '');
      setIsPublic(list.isPublic !== undefined ? list.isPublic : true);
    } else {
      setName('');
      setDescription('');
      setIsPublic(true);
    }
  }, [list]);

  // Reset form when modal closes
  useEffect(() => {
    if (!isOpen) {
      setName('');
      setDescription('');
      setIsPublic(true);
    }
  }, [isOpen]);

  // Create list mutation
  const createMutation = useMutation({
    mutationFn: (data) => savedAPI.createList(data),
    onSuccess: () => {
      queryClient.invalidateQueries(createQueryKey.savedLists());
      queryClient.invalidateQueries(['saved-lists-page']); // Also invalidate the page query
      queryClient.invalidateQueries(['saved-lists-profile']); // Invalidate profile page query
      onClose();
    },
    onError: (error) => {
      toast.error('Failed to create list: ' + (error.response?.data?.detail || error.message));
    }
  });

  // Update list mutation
  const updateMutation = useMutation({
    mutationFn: (data) => savedAPI.updateList(list.id, data),
    onSuccess: () => {
      queryClient.invalidateQueries(createQueryKey.savedLists());
      queryClient.invalidateQueries(createQueryKey.savedList(list.id));
      queryClient.invalidateQueries(['saved-lists-page']); // Also invalidate the page query
      queryClient.invalidateQueries(['saved-lists-profile']); // Invalidate profile page query
      onClose();
    },
    onError: (error) => {
      toast.error('Failed to update list: ' + (error.response?.data?.detail || error.message));
    }
  });

  // Delete list mutation
  const deleteMutation = useMutation({
    mutationFn: (listId) => savedAPI.deleteList(listId),
    onMutate: async (listIdToDelete) => {
      // Check if we're currently viewing this list and navigate away if so
      const currentPath = window.location.pathname;
      if (currentPath.includes(`/saved/${listIdToDelete}`)) {
        navigate('/saved');
      }
      
      // Cancel any outgoing refetches for the list being deleted
      await queryClient.cancelQueries(createQueryKey.savedList(listIdToDelete));
    },
    onSuccess: () => {
      queryClient.invalidateQueries(['saved-lists-page']);
      queryClient.invalidateQueries(['saved-lists-profile']); // Invalidate profile page query
      queryClient.invalidateQueries(createQueryKey.savedLists());
      setShowDeleteConfirm(false);
      onClose();
    },
    onError: (error) => {
      toast.error('Failed to delete list: ' + (error.response?.data?.detail || error.message));
    }
  });

  const handleSubmit = (e) => {
    e.preventDefault();
    
    if (!name.trim()) {
      toast.error('List name is required');
      return;
    }

    const data = {
      name: name.trim(),
      description: description.trim() || null,
      isPublic
    };

    if (mode === 'create') {
      createMutation.mutate(data);
    } else {
      updateMutation.mutate(data);
    }
  };

  const handleDeleteClick = () => {
    setShowDeleteConfirm(true);
  };

  const confirmDelete = () => {
    if (list) {
      deleteMutation.mutate(list.id);
    }
  };

  const cancelDelete = () => {
    setShowDeleteConfirm(false);
  };

  const isLoading = createMutation.isLoading || updateMutation.isLoading;
  const isDefault = list?.isDefault;

  // Animation variants for bottom slide (mobile) and fade/scale (desktop)
  const modalVariants = {
    hidden: isMobile ? {
      y: '100%',
      opacity: 0,
    } : {
      y: 20,
      opacity: 0,
      scale: 0.95,
    },
    visible: {
      y: 0,
      opacity: 1,
      scale: 1,
    },
    exit: isMobile ? {
      y: '100%',
      opacity: 0,
    } : {
      y: 20,
      opacity: 0,
      scale: 0.95,
    },
  };

  // Background variants for fade effect
  const backgroundVariants = {
    hidden: { opacity: 0 },
    visible: { opacity: 1 },
    exit: { opacity: 0 },
  };

  // Animation transition settings  
  const transition = {
    type: 'tween',
    ease: [0.4, 0, 0.2, 1], // Material Design easing
    duration: isMobile ? 0.35 : 0.25, // Slightly longer for mobile slide, shorter for desktop fade
  };

  // Background transition (faster fade)
  const backgroundTransition = {
    type: 'tween',
    ease: 'easeOut',
    duration: 0.2,
  };

  return createPortal(
    <>
      <AnimatePresence>
        {isOpen && (
          <motion.div 
            key="main-modal"
            className={`fixed inset-0 bg-black bg-opacity-50 z-[50001] ${
              isMobile 
                ? 'flex items-end justify-center' 
                : 'flex items-center justify-center p-4'
            }`}
            variants={backgroundVariants}
            initial="hidden"
            animate="visible"
            exit="exit"
            transition={backgroundTransition}
            onClick={onClose}
          >
          <motion.div 
            className={`bg-white dark:bg-gray-800 w-full flex flex-col ${
              isMobile 
                ? 'rounded-t-xl' 
                : 'rounded-lg max-w-md max-h-[80vh]'
            }`}
            style={isMobile ? {
              height: 'auto',
              maxHeight: '75dvh',
              paddingBottom: 'env(safe-area-inset-bottom)'
            } : {}}
            variants={modalVariants}
            initial="hidden"
            animate="visible"
            exit="exit"
            transition={transition}
            onClick={(e) => e.stopPropagation()}
          >
            <div className="p-6 flex-1 overflow-y-auto">
              {/* Header */}
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-2xl font-semibold text-gray-900 dark:text-white">
                  {mode === 'create' ? 'Create new list' : 'Edit list'}
                </h2>
                <button
                  onClick={onClose}
                  className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-full transition-colors"
                >
                  <XMarkIcon className="w-5 h-5 text-gray-500 dark:text-gray-400" />
                </button>
              </div>

              {/* Form */}
              <form onSubmit={handleSubmit} className="space-y-4">
                {/* Name field */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    List name *
                  </label>
                  <input
                    type="text"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    placeholder="e.g., Weekend Hikes"
                    disabled={isDefault} // Prevent editing default list name
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent disabled:opacity-50 disabled:cursor-not-allowed"
                    maxLength={100}
                    required
                  />
                  {isDefault && (
                    <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                      Default list name cannot be changed
                    </p>
                  )}
                </div>

                {/* Description field */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Description
                  </label>
                  <textarea
                    value={description}
                    onChange={(e) => setDescription(e.target.value)}
                    placeholder="Optional description for your list..."
                    rows={3}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    maxLength={500}
                  />
                  <div className="text-xs text-gray-500 dark:text-gray-400 mt-1 text-right">
                    {description.length}/500
                  </div>
                </div>

                {/* Privacy setting */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Privacy
                  </label>
                  <select
                    value={isPublic ? 'public' : 'private'}
                    onChange={(e) => setIsPublic(e.target.value === 'public')}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  >
                    <option value="public">Public</option>
                    <option value="private">Private</option>
                  </select>
                </div>

                {/* Delete button - only show in edit mode for non-default lists */}
                {mode === 'edit' && !isDefault && (
                  <button
                    type="button"
                    onClick={handleDeleteClick}
                    className="px-4 py-3 w-full text-red-600 dark:text-red-400 hover:text-red-700 dark:hover:text-red-300 border border-red-300 dark:border-red-600 rounded-md hover:bg-red-50 dark:hover:bg-red-900/20 transition-colors flex items-center justify-center gap-2"
                  >
                    <TrashIcon className="w-4 h-4" />
                    Delete List
                  </button>
                )}
              </form>
            </div>

            {/* Footer Buttons */}
            <div className="p-4 border-t border-gray-200 dark:border-gray-700">
              <div className="flex gap-3">
                <button
                  type="button"
                  onClick={onClose}
                  className="flex-1 px-4 py-3 text-gray-600 dark:text-gray-400 hover:text-gray-800 dark:hover:text-gray-200 font-medium"
                >
                  Cancel
                </button>
                <button
                  onClick={handleSubmit}
                  disabled={isLoading || !name.trim()}
                  className="flex-1 bg-blue-600 hover:bg-blue-700 dark:bg-blue-500 dark:hover:bg-blue-600 text-white px-4 py-3 rounded-md transition-colors font-medium disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center"
                >
                  {isLoading ? (
                    <>
                      <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2"></div>
                      {mode === 'create' ? 'Creating...' : 'Updating...'}
                    </>
                  ) : (
                    mode === 'create' ? 'Create List' : 'Update List'
                  )}
                </button>
              </div>
            </div>
          </motion.div>
        </motion.div>
      )}
      </AnimatePresence>

      {/* Delete Confirmation Dialog */}
      <AnimatePresence>
        {showDeleteConfirm && (
          <motion.div 
            key="delete-modal"
            className="fixed inset-0 bg-black bg-opacity-50 z-[50002] flex items-center justify-center p-4"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }}
            onClick={cancelDelete}
          >
          <motion.div 
            className="bg-white dark:bg-gray-800 rounded-lg p-6 max-w-md w-full"
            initial={{ scale: 0.95, opacity: 0, y: 20 }}
            animate={{ scale: 1, opacity: 1, y: 0 }}
            exit={{ scale: 0.95, opacity: 0, y: 20 }}
            transition={{ duration: 0.2 }}
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center gap-3 mb-4">
              <ExclamationTriangleIcon className="w-6 h-6 text-red-600 dark:text-red-400 flex-shrink-0" />
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                Delete List
              </h3>
            </div>
            <p className="text-gray-600 dark:text-gray-400 mb-6">
              Are you sure you want to delete "{list?.name}"? This action cannot be undone.
            </p>
            <div className="flex gap-3 justify-end">
              <button
                onClick={cancelDelete}
                className="px-4 py-2 text-gray-600 dark:text-gray-400 hover:text-gray-800 dark:hover:text-gray-200 font-medium"
              >
                Cancel
              </button>
              <button
                onClick={confirmDelete}
                disabled={deleteMutation.isLoading}
                className="px-4 py-2 bg-red-600 hover:bg-red-700 dark:bg-red-500 dark:hover:bg-red-600 text-white rounded-md font-medium disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center gap-2"
              >
                {deleteMutation.isLoading ? (
                  <>
                    <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                    <span>Deleting...</span>
                  </>
                ) : (
                  <>
                    <TrashIcon className="w-4 h-4" />
                    <span>Delete</span>
                  </>
                )}
              </button>
            </div>
          </motion.div>
        </motion.div>
      )}
      </AnimatePresence>
    </>,
    document.body
  );
};

export default SavedListModal;
